import { defineStore, storeToRefs } from "pinia";
import dayjs from "dayjs";
import api from "@/services/api";
import { useItemsStore, useSessionStore, useSitesStore } from "@/stores";
import type IStockChange from "@/models/responses/IStockChange";
import StockChange from "@/models/frontend/StockChange";
import IPagedList from "@/models/responses/IPagedList";
import IItemSignatureRaw from "@/models/responses/IItemSignatureRaw";

export const useStockChangeStore = defineStore("stockChanges", {
  state: () => ({
    error: "" as any,
    data: [] as StockChange[],
    continuationToken: "",
    moreItems: false,
  }),
  actions: {
    async fetchPage(top: number) {
      const sessionStore = useSessionStore();
      const { selectedSiteId } = storeToRefs(sessionStore);
      this.continuationToken = "";
      this.error = "";
      await api
        .get<IPagedList<IStockChange>>(`v1.0/stock-changes?siteId=${selectedSiteId.value}&top=${top}&continuationToken=${this.continuationToken}`)
        .then((response) => {
          this.continuationToken = response.data.continuationToken;
          this.moreItems = response.data.items.length === top;
          return (this.data = response.data.items
            .map((stockChange) => this.mapToFrontend(stockChange))
            .sort((a, b) => a.id > b.id ? -1 : (a.id < b.id ? 1 : 0))
          );
        })
        .catch((error) => (this.error = error));
    },
    async fetchPageAdditive(top: number) {
      const sessionStore = useSessionStore();
      const { selectedSiteId } = storeToRefs(sessionStore);

      this.error = "";
      await api
        .get<IPagedList<IStockChange>>(`v1.0/stock-changes?siteId=${selectedSiteId.value}&top=${top}&continuationToken=${this.continuationToken}`)
        .then((response) => {
          this.continuationToken = response.data.continuationToken;
          this.moreItems = response.data.items.length === top;
          return (response.data.items
            .map((stockChange) => this.mapToFrontend(stockChange))
            .sort((a, b) => a.id > b.id ? -1 : (a.id < b.id ? 1 : 0))
            .forEach((stockChange) => this.data.push(stockChange))
          );
        })
        .catch((error) => (this.error = error));
    },
    async fetchPageByItem(itemId: number, top: number) {
      const sessionStore = useSessionStore();
      const { selectedSiteId } = storeToRefs(sessionStore);
      this.continuationToken = "";
      this.error = "";
      await api
        .get<IPagedList<IStockChange>>(`v1.0/stock-changes?siteId=${selectedSiteId.value}&itemId=${itemId}&top=${top}&continuationToken=${this.continuationToken}`)
        .then((response) => {
          this.continuationToken = response.data.continuationToken;
          this.moreItems = response.data.items.length === top;
          return (this.data = response.data.items
            .map((stockChange) => this.mapToFrontend(stockChange))
            .sort((a, b) => a.id > b.id ? -1 : (a.id < b.id ? 1 : 0))
          );
        })
        .catch((error) => (this.error = error));
    },
    async fetchPageAdditiveByItem(itemId: number, top: number) {
      const sessionStore = useSessionStore();
      const { selectedSiteId } = storeToRefs(sessionStore);

      this.error = "";
      await api
        .get<IPagedList<IStockChange>>(`v1.0/stock-changes?siteId=${selectedSiteId.value}&itemId=${itemId}&top=${top}&continuationToken=${this.continuationToken}`)
        .then((response) => {
          this.continuationToken = response.data.continuationToken;
          this.moreItems = response.data.items.length === top;
          return (response.data.items
            .map((stockChange) => this.mapToFrontend(stockChange))
            .sort((a, b) => a.id > b.id ? -1 : (a.id < b.id ? 1 : 0))
            .forEach((stockChange) => this.data.push(stockChange))
          );
        })
        .catch((error) => (this.error = error));
    },
    async undoStockChange(id: number) {
      this.error = "";
      try {
        await api.post<IStockChange>(`v1.0/stock-changes/${id}/.undo`);
      }
      catch (error) {
        this.error = error;
        throw error;
      }
    },
    async fetchSignaturesForItem(itemId: number) {
      const sessionStore = useSessionStore();
      const { selectedSiteId } = storeToRefs(sessionStore);
      this.error = "";
      let signatures: IItemSignatureRaw[] = [];
      await api
        .get<IItemSignatureRaw[]>(`v1.0/stock-changes/signatures?siteId=${selectedSiteId.value}&itemId=${itemId}`)
        .then((response) => (signatures = response.data))
        .catch((error) => (this.error = error));
      return signatures;
    },
    mapToFrontend(stockChange: IStockChange) {
      const sitesStore = useSitesStore();
      const itemsStore = useItemsStore();

      const site = sitesStore.getById(stockChange.siteId);
      const item = itemsStore.getById(stockChange.itemId);

      const m_bestBefore = dayjs(stockChange.bestBefore);
      const m_expiration = dayjs(stockChange.expiry);
      const now = dayjs(stockChange.audit.createdAt);
      const bestBeforeExceeded = m_bestBefore.isBefore(now);
      const expirationExceeded = m_expiration.isBefore(now);
      const bestBeforeLeadTimeExceeded = m_bestBefore
        .subtract(item.bestBeforeLeadTime, "day")
        .isBefore(now);
      const expirationLeadTimeExceeded = m_expiration
        .subtract(item.expirationLeadTime, "day")
        .isBefore(now);
    
      return new StockChange(
        stockChange.id,
        site,
        item,
        stockChange.realativeQuantity,
        stockChange.quantity,
        stockChange.lot,
        stockChange.bestBefore,
        stockChange.expiry,
        optionalDate(stockChange.bestBefore),
        optionalDate(stockChange.expiry),
        bestBeforeExceeded,
        expirationExceeded,
        bestBeforeLeadTimeExceeded,
        expirationLeadTimeExceeded,
        stockChange.audit
      );
    }
  },
  getters: {
    getItemById: (state) => (id: number) =>
      state.data.find((stockChange) => stockChange.id === id),
    getItemByItemId: (state) => (itemId: number) =>
      state.data.find((stockChange) => stockChange.item.id === itemId),
  },
});

const optionalDate = (date: string | undefined) => {
  return date ? new Date(date) : undefined;
}
